import type { SelectionRange, WordRunFn } from "../global";
import type { SelectionRangeFactory } from "./SelectionRangeFactory";
import { ProgressiveDebounce } from "./ProgressiveDebounce";

export type SelectionPhase = "unregistered" | "idle" | "debouncing" | "querying" | "paused";

export interface SelectionHandlerOptions {
  run: WordRunFn;
  debounceMs: number;
  debounceMultiplier?: number;
  fetchSelection: (signal: AbortSignal) => Promise<SelectionRange | null>;
  factory: SelectionRangeFactory;
}

export class SelectionHandler {
  private _phase: SelectionPhase = "unregistered";
  private _fetchSelection: ((signal: AbortSignal) => Promise<SelectionRange | null>) | null = null;
  private _factory: SelectionRangeFactory | null = null;
  private _debounce: ProgressiveDebounce | null = null;
  private _abortController: AbortController | null = null;
  private _phaseListeners: Set<(phase: SelectionPhase, prev: SelectionPhase) => void> = new Set();
  private _selectionListeners: Set<(sel: SelectionRange | null) => void> = new Set();
  private _runStartTime: number = 0;

  constructor() { }

  /** Current state machine phase. */
  get phase(): SelectionPhase {
    return this._phase;
  }

  /** Factory for creating selection ranges. */
  get factory(): SelectionRangeFactory | null {
    return this._factory;
  }

  /** Initializes handler with options; transitions from unregistered to idle. */
  async register(options: SelectionHandlerOptions): Promise<void> {
    if (this._phase !== "unregistered") {
      console.log("[SelectionHandler] Already registered, ignoring");
      return;
    }
    this._fetchSelection = options.fetchSelection;
    this._factory = options.factory;
    this._debounce = new ProgressiveDebounce(options.debounceMs, options.debounceMultiplier ?? 2);
    console.log("[SelectionHandler] Registered with debounce:", options.debounceMs, "multiplier:", options.debounceMultiplier ?? 2);
    this.transition("idle");
  }

  /** Cancels any pending operation and transitions to paused. */
  pause(): void {
    if (this._phase === "unregistered") {
      console.log("[SelectionHandler] Pause ignored, not registered");
      return;
    }
    console.log("[SelectionHandler] Pausing from:", this._phase);
    this.abortCurrent();
    this.clearDebounce();
    this.transition("paused");
  }

  /** Resumes from paused to idle. */
  resume(): void {
    if (this._phase === "unregistered") {
      console.log("[SelectionHandler] Resume ignored, not registered");
      return;
    }
    if (this._phase !== "paused") {
      console.log("[SelectionHandler] Resume ignored, not paused (phase:", this._phase, ")");
      return;
    }
    console.log("[SelectionHandler] Resuming");
    this.transition("idle");
  }

  /** Debounces selection change events, then fetches and notifies listeners. */
  handleSelectionChanged(): void {
    if (this._phase !== "idle" && this._phase !== "debouncing") {
      console.log("[SelectionHandler] Selection change ignored, phase:", this._phase);
      return;
    }

    this.transition("debouncing");

    this._debounce!.schedule(async () => {
      this._runStartTime = Date.now();
      console.log("[SelectionHandler] Debounce complete, fetching selection");
      this.transition("querying");

      this._abortController = new AbortController();
      const signal = this._abortController.signal;

      try {
        const selection = await this._fetchSelection!(signal);

        if (!signal.aborted) {
          const elapsed = Date.now() - this._runStartTime;
          this._debounce!.recordExecution(elapsed);
          console.log("[SelectionHandler] Selection fetched:", selection, "elapsed:", elapsed, "ms, next debounce:", this._debounce!.getCurrentDebounce(), "ms");
          this.notifySelectionListeners(selection);
          this.transition("idle");
        } else {
          console.log("[SelectionHandler] Fetch aborted, result discarded");
        }
      } catch (error) {
        console.error("[SelectionHandler] Error fetching selection:", error);
        if (!signal.aborted) {
          this.transition("idle");
        }
      } finally {
        this._abortController = null;
      }
    });
  }

  /** Subscribes to phase changes; returns unsubscribe function. */
  onPhaseChange(cb: (phase: SelectionPhase, prev: SelectionPhase) => void): () => void {
    this._phaseListeners.add(cb);
    return () => {
      this._phaseListeners.delete(cb);
    };
  }

  /** Subscribes to selection updates; returns unsubscribe function. */
  onSelection(cb: (sel: SelectionRange | null) => void): () => void {
    this._selectionListeners.add(cb);
    return () => {
      this._selectionListeners.delete(cb);
    };
  }

  /** Updates phase and notifies all phase listeners. */
  private transition(phase: SelectionPhase): void {
    const prev = this._phase;
    this._phase = phase;
    console.log("[SelectionHandler] Phase:", prev, "->", phase);

    for (const listener of this._phaseListeners) {
      listener(phase, prev);
    }
  }

  /** Clears any pending debounce timer. */
  private clearDebounce(): void {
    if (this._debounce) {
      console.log("[SelectionHandler] Debounce timer cleared");
      this._debounce.clear();
    }
  }

  /** Aborts any in-flight selection fetch. */
  private abortCurrent(): void {
    if (this._abortController) {
      console.log("[SelectionHandler] Aborting current fetch");
      this._abortController.abort();
      this._abortController = null;
    }
  }

  /** Notifies all selection listeners with the fetched selection. */
  private notifySelectionListeners(sel: SelectionRange | null): void {
    for (const listener of this._selectionListeners) {
      listener(sel);
    }
  }
}
